/**
 * Almari · Outfit recommender.
 *
 * Picks today's outfit by joining three signals:
 *   1. Cost-per-wear ROI (push under-utilised quality pieces)
 *   2. Weather (Open-Meteo, free, no key) — current temp + precipitation
 *   3. Anchored colour (top palette colour)
 *
 * Output is a deterministic ranked list: shirt + pant + shoes + accessory.
 * Calm-tech: no notifications. Renders ambient tile only.
 */
import { store } from '../store.ts';
import { formatINR } from '../types.ts';
import type { ClothingItem } from '../types.ts';

interface WeatherSnapshot {
  readonly temp_c: number;
  readonly precip_mm: number;
  readonly note: string;
}

async function fetchWeather(): Promise<WeatherSnapshot> {
  try {
    const pos: GeolocationPosition | null = await new Promise((res) => {
      if (!('geolocation' in navigator)) { res(null); return; }
      navigator.geolocation.getCurrentPosition(
        (p) => res(p),
        () => res(null),
        { enableHighAccuracy: false, maximumAge: 30 * 60 * 1000, timeout: 4000 },
      );
    });
    const lat = pos?.coords.latitude ?? 19.0760; // Bombay
    const lon = pos?.coords.longitude ?? 72.8777;
    const r = await fetch(`https://api.open-meteo.com/v1/forecast?latitude=${lat}&longitude=${lon}&current=temperature_2m,precipitation,weather_code`);
    if (!r.ok) throw new Error('weather fetch failed');
    const j = await r.json() as { current: { temperature_2m: number; precipitation: number } };
    const temp = j.current.temperature_2m;
    const precip = j.current.precipitation ?? 0;
    return {
      temp_c: temp,
      precip_mm: precip,
      note: precip > 1 ? 'baarish — water-resistant chuno'
        : temp >= 32 ? 'garmi — linen ya cotton chuno'
          : temp <= 16 ? 'thand — layering chuno'
            : 'mausam zara mild — light fabric',
    };
  } catch {
    return { temp_c: 28, precip_mm: 0, note: 'weather offline — defaulting to mild' };
  }
}

function pickByCategory(items: ClothingItem[], category: ClothingItem['category'], temp: number, precip: number): ClothingItem | null {
  const candidates = items.filter((i) => i.category === category);
  if (!candidates.length) return null;
  const scored = candidates.map((i) => {
    let score = 0;
    // ROI bonus: lower cost-per-wear = better
    score += 100 / Math.max(i.cost_per_wear, 1);
    // weather-fabric heuristic
    const heavy = /wool|blazer|sherwani|denim/i.test(i.name);
    const light = /linen|cotton|polo|henley|tee|shorts/i.test(i.name);
    if (temp >= 30 && heavy) score -= 25;
    if (temp <= 16 && light) score -= 15;
    if (precip > 1 && /silk|suede|sherwani|kurta/i.test(i.name)) score -= 30;
    // anti-recent: items worn in last 3 days get a soft penalty
    if (i.last_worn) {
      const days = Math.floor((Date.now() - new Date(i.last_worn).getTime()) / 86_400_000);
      if (days < 3) score -= 20;
    }
    return { i, score };
  });
  scored.sort((a, b) => b.score - a.score);
  return scored[0]?.i ?? null;
}

export class AlmariOutfitRecommender extends HTMLElement {
  #shadow = this.attachShadow({ mode: 'open' });
  #unsub?: () => void;
  #weather: WeatherSnapshot | null = null;

  async connectedCallback(): Promise<void> {
    this.#weather = await fetchWeather();
    this.#unsub = store.subscribe((s) => {
      if (s.summary) this.#render(s.summary.items);
    });
  }

  disconnectedCallback(): void { this.#unsub?.(); }

  #render(items: ClothingItem[]): void {
    if (!this.#weather) return;
    const { temp_c, precip_mm, note } = this.#weather;

    const top = pickByCategory(items, 'shirts', temp_c, precip_mm) ?? pickByCategory(items, 'ethnic_kurta', temp_c, precip_mm);
    const bottom = pickByCategory(items, 'pants', temp_c, precip_mm);
    const shoes = pickByCategory(items, 'shoes', temp_c, precip_mm);
    const acc = pickByCategory(items, 'accessories', temp_c, precip_mm);

    const tile = (label: string, it: ClothingItem | null): string => it ? `
      <article class="tile">
        <div class="tile-no">${label}</div>
        <div class="tile-name">${escapeHtml(it.name)}</div>
        <div class="tile-brand">${escapeHtml(it.brand)}</div>
        <div class="tile-cpw">${formatINR(it.cost_per_wear)}/w · ${it.wear_count} wears</div>
      </article>` : `
      <article class="tile tile-empty">
        <div class="tile-no">${label}</div>
        <div class="tile-empty-msg">koi fit nahi mila</div>
      </article>`;

    this.#shadow.innerHTML = `
<style>
:host { display: block; }
.head { padding-left: 4px; margin-bottom: 12px; display: grid; gap: 4px; }
.eyebrow { font-family: var(--mono); font-size: 9px; letter-spacing: 0.42em; text-transform: uppercase; color: var(--brass-dim); }
.title { font-family: var(--display); font-style: italic; font-size: 22px; color: var(--ink); font-weight: 600; letter-spacing: -0.005em; }
.weather {
  display: flex; gap: 18px; align-items: baseline; padding: 14px 16px;
  background: color-mix(in oklch, var(--brass) 4%, var(--paper-card));
  border: 1px solid var(--rule); border-radius: 8px; margin-bottom: 12px;
}
.temp { font-family: var(--display); font-size: 32px; font-weight: 700; color: var(--ink-deep); letter-spacing: -0.02em; font-feature-settings: 'tnum' 1; }
.cond { font-family: var(--display); font-style: italic; font-size: 16px; color: var(--ink-dim); text-wrap: pretty; }
.grid { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; }
@container (min-width: 460px) { .grid { grid-template-columns: repeat(4, 1fr); } }
.tile {
  background: var(--paper-card); border: 1px solid var(--rule); border-radius: 6px;
  padding: 14px; display: grid; gap: 4px; isolation: isolate; position: relative;
}
.tile-no { font-family: var(--mono); font-size: 8px; letter-spacing: 0.32em; text-transform: uppercase; color: var(--brass-dim); }
.tile-name {
  font-family: var(--display); font-size: 17px; color: var(--ink); font-weight: 700;
  letter-spacing: -0.005em; line-height: 1.1; text-wrap: balance;
  overflow: hidden; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical;
}
.tile-brand { font-family: var(--display); font-style: italic; font-size: 13px; color: var(--brass-dim); }
.tile-cpw { font-family: var(--mono); font-size: 9px; letter-spacing: 0.14em; text-transform: uppercase; color: var(--ink-faint); margin-top: 4px; }
.tile-empty { background: var(--paper-up); }
.tile-empty-msg { font-family: var(--display); font-style: italic; font-size: 14px; color: var(--ink-faint); }
</style>
<header class="head">
  <span class="eyebrow">Aaj kya pehna jaaye · weather + ROI</span>
  <h3 class="title">Today's edit, hand-picked.</h3>
</header>
<div class="weather">
  <span class="temp">${Math.round(temp_c)}°</span>
  <span class="cond">${escapeHtml(note)}</span>
</div>
<div class="grid">
  ${tile('shirt', top)}
  ${tile('pant', bottom)}
  ${tile('shoes', shoes)}
  ${tile('accent', acc)}
</div>
`;
  }
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c] ?? c));
}

customElements.define('almari-outfit-recommender', AlmariOutfitRecommender);
